import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiFetch, apiImageUrl } from '../utils/streamApi';

// Camera Test Mode (design §31) — full-screen installer diagnostic (like the
// Object Intelligence config). Shows the live frame with the drawn zone + the
// detected skeleton overlaid, and a real-time readout so the installer can stand
// at the checkpoint (day or night) and confirm a raised hand actually registers.

const COCO_EDGES = [[5, 7], [7, 9], [6, 8], [8, 10], [5, 6], [5, 11], [6, 12], [11, 12], [11, 13], [13, 15], [12, 14], [14, 16]];

export default function PatrolTestModePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [checkpoints, setCheckpoints] = useState([]);
  const [sel, setSel]         = useState('');
  const [imgUrl, setImgUrl]   = useState('');
  const [readout, setReadout] = useState(null);
  const [heldMs, setHeldMs]   = useState(0);
  const imgRef     = useRef(null);
  const canvasRef  = useRef(null);
  const holdStart  = useRef(null);
  const lastRaised = useRef(0);
  const busy       = useRef(false);

  useEffect(() => {
    apiFetch('/ui/patrol/config').then(r => r.ok ? r.json() : null).then(cfg => {
      if (!cfg) return;
      const list = [];
      (cfg.routes || []).forEach(rt => (rt.checkpoints || []).forEach(cp =>
        list.push({ key: `${rt.id}|${cp.id}`, routeName: rt.name, cp })));
      setCheckpoints(list);
      const pre = location.state?.key;
      setSel(pre && list.find(x => x.key === pre) ? pre : (list[0]?.key || ''));
    });
  }, [location.state]);

  const current   = checkpoints.find(x => x.key === sel);
  const cameraIdx = current?.cp?.camera_idx;
  const polygon   = useMemo(() => current?.cp?.patrol_zone || [], [current]);
  const holdReq   = Number(current?.cp?.gesture_hold_seconds || 2);
  const hasCam    = cameraIdx !== undefined && cameraIdx !== '' && cameraIdx !== null;

  const refreshFrame = useCallback(async () => {
    if (!hasCam) return;
    try { setImgUrl(await apiImageUrl(`/cameras/${cameraIdx}/frame?ts=${Date.now()}`)); } catch { /* keep last */ }
  }, [cameraIdx, hasCam]);

  useEffect(() => {
    if (!hasCam) return;
    refreshFrame();
    const t = setInterval(refreshFrame, 800);
    return () => clearInterval(t);
  }, [refreshFrame, hasCam]);

  // clear stale frame/readout immediately when the camera changes (no lingering
  // last-camera image while the new one loads on a busy box)
  useEffect(() => {
    setImgUrl(''); setReadout(null);
    holdStart.current = null; lastRaised.current = 0; busy.current = false; setHeldMs(0);
  }, [cameraIdx]);

  useEffect(() => {
    if (!hasCam) { setReadout(null); return; }
    let alive = true;
    const poll = async () => {
      if (busy.current) return;   // never overlap — a slow box would pile requests up
      busy.current = true;
      try {
        const r = await apiFetch('/ui/patrol/test', {
          method: 'POST',
          json: { camera_idx: Number(cameraIdx), polygon, expected_guard: current?.cp?.expected_guard || null },
        });
        if (!alive) return;
        const d = r.ok ? await r.json() : null;
        setReadout(d);
        const now = Date.now();
        // Timer tracks the GESTURE itself (moves whenever a hand is up); READY below
        // additionally requires being in the zone.
        const raised = !!(d && (d.left_raised || d.right_raised));
        if (raised) {
          lastRaised.current = now;
          if (!holdStart.current) holdStart.current = now;
          setHeldMs(now - holdStart.current);
        } else if (holdStart.current && now - lastRaised.current > 800) {
          holdStart.current = null; setHeldMs(0);   // tolerate a brief keypoint dropout
        }
      } catch { /* transient */ }
      finally { busy.current = false; }
    };
    poll();
    const t = setInterval(poll, 500);
    return () => { alive = false; clearInterval(t); };
  }, [cameraIdx, sel]); // eslint-disable-line react-hooks/exhaustive-deps

  // overlay: zone polygon + detected skeletons
  useEffect(() => {
    const cv = canvasRef.current, img = imgRef.current;
    if (!cv || !img) return;
    cv.width = img.clientWidth; cv.height = img.clientHeight;
    const ctx = cv.getContext('2d');
    const W = cv.width, H = cv.height;
    ctx.clearRect(0, 0, W, H);
    if (polygon.length >= 3) {
      ctx.beginPath();
      polygon.forEach(([x, y], i) => { const px = x * W, py = y * H; i ? ctx.lineTo(px, py) : ctx.moveTo(px, py); });
      ctx.closePath();
      ctx.strokeStyle = readout?.in_zone ? '#5cc98a' : '#4da3ff';
      ctx.fillStyle   = readout?.in_zone ? 'rgba(92,201,138,0.15)' : 'rgba(77,163,255,0.12)';
      ctx.lineWidth = 2; ctx.fill(); ctx.stroke();
    }
    (readout?.persons || []).forEach(p => {
      const kp = p.kpts;
      ctx.strokeStyle = '#ffd24d'; ctx.lineWidth = 2;
      COCO_EDGES.forEach(([a, b]) => {
        if (kp[a][2] >= 0.3 && kp[b][2] >= 0.3) {
          ctx.beginPath(); ctx.moveTo(kp[a][0] * W, kp[a][1] * H); ctx.lineTo(kp[b][0] * W, kp[b][1] * H); ctx.stroke();
        }
      });
      ctx.fillStyle = '#ffd24d';
      kp.forEach(k => { if (k[2] >= 0.3) { ctx.beginPath(); ctx.arc(k[0] * W, k[1] * H, 3, 0, 6.2832); ctx.fill(); } });
      const [x1, y1, x2, y2] = p.box;
      ctx.strokeStyle = 'rgba(255,255,255,0.45)'; ctx.lineWidth = 1;
      ctx.strokeRect(x1 * W, y1 * H, (x2 - x1) * W, (y2 - y1) * H);
    });
  }, [readout, polygon]);

  const held  = heldMs / 1000;
  const ready = !!(readout?.in_zone && readout?.pose_available && held >= holdReq);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#0b0b0b', color: '#eee' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 16px', flexWrap: 'wrap', borderBottom: '1px solid #222' }}>
        <button onClick={() => navigate('/guard-patrol')} style={btn}>← Config</button>
        <b style={{ fontSize: 16 }}>🎯 Camera Test Mode</b>
        <select value={sel} onChange={e => setSel(e.target.value)} style={{ ...btn, minWidth: 220 }}>
          {checkpoints.length === 0 && <option value="">No checkpoints configured</option>}
          {checkpoints.map(c => <option key={c.key} value={c.key}>{c.routeName} · {c.cp.name}</option>)}
        </select>
        <span style={{ color: '#888', fontSize: 13 }}>
          Stand in the zone (test day AND night) and raise a hand to confirm it registers.
        </span>
      </header>

      {checkpoints.length === 0 ? (
        <div style={{ flex: 1, display: 'grid', placeItems: 'center', color: '#888' }}>
          Configure a route + checkpoint first, then come back to test it.
        </div>
      ) : (
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden', background: '#000' }}>
          {imgUrl
            ? <img key={cameraIdx} ref={imgRef} src={imgUrl} alt="live" onLoad={() => { /* redraw driven by readout tick */ }}
                   style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }} />
            : <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: '#666' }}>Loading camera…</div>}
          <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />

          {/* readout */}
          <div style={panel}>
            <Row label="Person detected"   ok={readout?.person_detected} value={readout?.person_detected ? 'YES' : 'NO'} />
            <Row label="Inside patrol zone" ok={readout?.in_zone}         value={readout?.in_zone ? 'YES' : 'NO'} />
            <Row label="Pose available"    ok={readout?.pose_available}   value={readout?.pose_available ? 'YES' : 'NO'} />
            <Row label="Left hand"  ok={readout?.left_raised || null}  value={readout?.left_raised ? 'RAISED' : '—'} />
            <Row label="Right hand" ok={readout?.right_raised || null} value={readout?.right_raised ? 'RAISED' : '—'} />
            <Row label="Gesture held" ok={held >= holdReq || null} value={`${held.toFixed(1)} / ${holdReq.toFixed(1)} s`} />
            <Row label="Face" ok={readout?.face_status === 'VERIFIED' ? true : readout?.face_status ? null : undefined}
                 value={readout?.face_name ? `${readout.face_name}` : (readout?.face_status || '—')} />
          </div>

          {/* big status */}
          <div style={{
            position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)',
            padding: '10px 28px', borderRadius: 30, fontSize: 18, fontWeight: 700, letterSpacing: 0.5,
            background: ready ? 'rgba(92,201,138,0.9)' : 'rgba(40,40,40,0.85)',
            color: ready ? '#08130c' : '#ccc', border: `1px solid ${ready ? '#5cc98a' : '#333'}`,
          }}>
            {ready ? '✓ CHECK-IN READY' : 'WAITING…'}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, ok }) {
  const color = ok === true ? '#5cc98a' : ok === false ? '#e07070' : '#ddd';
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 18, padding: '7px 0', borderBottom: '1px solid #222' }}>
      <span style={{ color: '#999' }}>{label}</span>
      <span style={{ fontWeight: 600, color }}>{value ?? '—'}</span>
    </div>
  );
}

const btn = {
  padding: '7px 12px', background: '#1a1a1a', color: '#eee',
  border: '1px solid #333', borderRadius: 6, cursor: 'pointer', fontSize: 14,
};
const panel = {
  position: 'absolute', top: 16, right: 16, width: 260, padding: '12px 16px',
  background: 'rgba(15,15,15,0.86)', border: '1px solid #2a2a2a', borderRadius: 10,
  fontSize: 14, backdropFilter: 'blur(4px)',
};
